import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LiveRadio } from '@/mocks/liveRadios';
import { SwalAlert } from '@/hooks/useSweetAlert';

interface LiveRadioCardProps {
  radio: LiveRadio;
}

export default function LiveRadioCard({ radio }: LiveRadioCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  // liveRadios uses "102.7 FM" format; navigate to /radios-en-vivo since no 1:1 match with radios.ts
  const currentProgram = radio.programs[0];
  const mainBroadcaster = radio.broadcasters[0];
  const listenersFormatted = radio.currentListeners >= 1000
    ? `${(radio.currentListeners / 1000).toFixed(1)}K`
    : `${radio.currentListeners}`;

  const handleEnter = useCallback(() => {
    navigate('/radios-en-vivo');
  }, [navigate]);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/radios-en-vivo`;
    if (navigator.share) {
      try {
        await navigator.share({ title: radio.name, text: `Escucha ${radio.name} en vivo en RadioFrecuenciaWeb`, url: shareUrl });
      } catch { /* cancel */ }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      SwalAlert.toast('Enlace copiado al portapapeles', 'success');
    }
  }, [radio.name]);

  return (
    <article
      className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/80 hover:border-foreground-300/50 transition-all duration-300 cursor-pointer"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Cover Image + Overlay */}
      <div className="relative aspect-[16/10] overflow-hidden" onClick={handleEnter}>
        <img
          src={radio.thumbnailUrl}
          alt={radio.name}
          className={`w-full h-full object-cover object-top transition-transform duration-500 ${isHovered ? 'scale-105' : 'scale-100'}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/10"></div>

        {/* LIVE Badge */}
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
          <span className="text-[10px] font-extrabold text-background-50 tracking-widest uppercase bg-red-500/90 px-2.5 py-1 rounded-full backdrop-blur-sm">
            EN VIVO
          </span>
        </div>

        {/* RFW Frequency Badge */}
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-background-50 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <i className="ri-radio-line text-[10px]"></i>
            {radio.frequency}
          </span>
        </div>

        {/* UpTime + Chat */}
        <div className="absolute bottom-3 left-3 flex items-center gap-3">
          <span className="text-[10px] font-semibold text-background-50/80 flex items-center gap-1">
            <i className="ri-time-line text-[10px]"></i>
            {radio.upTime}
          </span>
          <span className="text-[10px] font-semibold text-background-50/80 flex items-center gap-1">
            <i className="ri-chat-3-line text-[10px]"></i>
            {radio.chatMsgCount >= 1000 ? `${(radio.chatMsgCount / 1000).toFixed(1)}K` : radio.chatMsgCount}
          </span>
        </div>

        {/* Listeners count */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1.5">
          <div className="flex items-center gap-1 text-background-50 bg-black/40 backdrop-blur-sm px-2.5 py-1 rounded-full">
            <i className="ri-headphone-line text-[10px]"></i>
            <span className="text-[10px] font-extrabold">{listenersFormatted}</span>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="p-4">
        {/* Radio Name */}
        <h3
          className="text-base font-extrabold text-foreground-950 leading-tight mb-1 group-hover:text-primary-600 transition-colors line-clamp-1"
          onClick={handleEnter}
        >
          {radio.name}
        </h3>

        {/* Category + Status */}
        <div className="flex items-center gap-2 mb-2.5">
          <span className="text-[10px] font-semibold text-foreground-400 bg-background-100 px-2 py-0.5 rounded-md whitespace-nowrap">
            {radio.category}
          </span>
          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md whitespace-nowrap ${
            radio.status === 'on-air' ? 'bg-green-50 text-green-600' :
            radio.status === 'live-event' ? 'bg-violet-50 text-violet-600' :
            radio.status === 'live-show' ? 'bg-amber-50 text-amber-600' :
            'bg-cyan-50 text-cyan-600'
          }`}>
            <i className={`text-[9px] ${
              radio.status === 'on-air' ? 'ri-broadcast-line' :
              radio.status === 'live-event' ? 'ri-calendar-event-line' :
              radio.status === 'live-show' ? 'ri-mic-line' :
              'ri-sparkling-line'
            }`}></i>
            {radio.status === 'on-air' ? 'Al Aire' : radio.status === 'live-event' ? 'Evento Live' : radio.status === 'live-show' ? 'Show Live' : 'Especial'}
          </span>
        </div>

        {/* Program Name */}
        {currentProgram && (
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-1 h-4 rounded-full bg-primary-500 shrink-0"></div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-foreground-800 truncate">{currentProgram.name}</p>
            </div>
          </div>
        )}

        {/* Host */}
        {currentProgram && (
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-user-voice-line text-[11px] text-foreground-400 shrink-0 w-3.5 h-3.5 flex items-center justify-center"></i>
            <p className="text-[11px] font-medium text-foreground-500 truncate">
              {currentProgram.host}
              {mainBroadcaster && mainBroadcaster.name !== currentProgram.host && (
                <span className="text-foreground-300"> · {mainBroadcaster.name}</span>
              )}
            </p>
          </div>
        )}

        {/* Scores Row */}
        <div className="flex items-center gap-3 mb-4 pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-1">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-flashlight-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{radio.activityScore}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Actividad</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-chat-smile-2-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{radio.interactionScore}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Interacción</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
              <i className="ri-headphone-line text-xs text-foreground-500"></i>
            </div>
            <div>
              <p className="text-xs font-extrabold text-foreground-950 leading-none">{listenersFormatted}</p>
              <p className="text-[9px] font-semibold text-foreground-400">Oyentes</p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleEnter}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-background-50 text-xs font-bold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-circle-line text-sm"></i>
            Entrar
          </button>
          <button
            onClick={handleShare}
            className="w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600 transition-all shrink-0"
            title="Compartir"
          >
            <i className="ri-share-forward-line text-sm"></i>
          </button>
        </div>
      </div>
    </article>
  );
}